import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  ArrowLeft,
  Bluetooth,
  BluetoothOff,
  Heart,
  Loader2,
  Zap,
  Watch,
  Footprints,
  Upload,
  ShieldCheck,
  Activity,
  Radio,
} from "lucide-react";
import { toast } from "sonner";
import { loadHealth, updateToday } from "@/lib/healthStore";

const API = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = "silentscan_wearable_device_v1";

// Standard BLE service UUIDs
const HR_SERVICE = "heart_rate";                // 0x180D
const HR_MEASUREMENT = "heart_rate_measurement"; // 0x2A37
const BATTERY_SERVICE = "battery_service";       // 0x180F
const BATTERY_LEVEL = "battery_level";           // 0x2A19
const DEVICE_INFO = "device_information";        // 0x180A

const readHeartRateMeasurement = (value) => {
  // BLE HR spec: first byte is flags, HR value is next 1 or 2 bytes
  const flags = value.getUint8(0);
  const is16 = flags & 0x1;
  return is16 ? value.getUint16(1, true) : value.getUint8(1);
};

const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = `dev-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
};

export default function Wearables() {
  const [supported] = useState(() => typeof navigator !== "undefined" && !!navigator.bluetooth);
  const [device, setDevice] = useState(null);
  const [deviceName, setDeviceName] = useState("");
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [hr, setHr] = useState(null);
  const [battery, setBattery] = useState(null);
  const [series, setSeries] = useState([]); // {t, bpm}
  const [stepsInput, setStepsInput] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const bufferRef = useRef([]);
  const deviceIdRef = useRef(getDeviceId());
  const hrCharRef = useRef(null);
  const flushTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      try {
        hrCharRef.current?.stopNotifications?.();
      } catch (e) {
        // already stopped
      }
      try {
        device?.gatt?.disconnect?.();
      } catch (e) {
        // already disconnected
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push a fresh HR sample into local state + buffer
  const onHR = (ev) => {
    try {
      const val = readHeartRateMeasurement(ev.target.value);
      if (!val || val < 30 || val > 250) return;
      setHr(val);
      const at = new Date().toISOString();
      setSeries((s) => [...s.slice(-59), { t: at.slice(11, 19), bpm: val }]);
      bufferRef.current.push({ bpm: val, at });
      // Also update today's HR in local dashboard store
      updateToday({ heart_rate: val });
    } catch (e) {
      // ignore
    }
  };

  const flushSamples = async () => {
    const samples = bufferRef.current.splice(0);
    if (samples.length === 0) return;
    try {
      await axios.post(`${API}/api/wearables/sync`, {
        device_id: deviceIdRef.current,
        device_name: deviceName || "BLE device",
        samples,
        battery: battery ?? undefined,
      });
      setLastSyncAt(new Date().toLocaleTimeString());
    } catch {
      // silent retry — samples not lost, they can accumulate on next flush window
      bufferRef.current.unshift(...samples);
    }
  };

  const connect = async () => {
    if (!supported) {
      toast.error("Your browser doesn't support Web Bluetooth. Use Chrome or Edge.");
      return;
    }
    setConnecting(true);
    try {
      const dev = await navigator.bluetooth.requestDevice({
        filters: [{ services: [HR_SERVICE] }],
        optionalServices: [BATTERY_SERVICE, DEVICE_INFO],
      });
      dev.addEventListener("gattserverdisconnected", () => {
        setConnected(false);
        toast.warning(`${dev.name || "Device"} disconnected`);
      });
      setDevice(dev);
      setDeviceName(dev.name || "BLE device");

      const server = await dev.gatt.connect();
      // Heart rate
      const hrSvc = await server.getPrimaryService(HR_SERVICE);
      const hrChar = await hrSvc.getCharacteristic(HR_MEASUREMENT);
      hrCharRef.current = hrChar;
      await hrChar.startNotifications();
      hrChar.addEventListener("characteristicvaluechanged", onHR);

      // Battery (optional)
      try {
        const batSvc = await server.getPrimaryService(BATTERY_SERVICE);
        const batChar = await batSvc.getCharacteristic(BATTERY_LEVEL);
        const bv = await batChar.readValue();
        setBattery(bv.getUint8(0));
      } catch {
        // battery service not supported
      }

      setConnected(true);
      toast.success(`Connected to ${dev.name || "device"} — streaming HR`);
      // Kick off a batched sync every 10s
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      flushTimerRef.current = setInterval(flushSamples, 10000);
    } catch (e) {
      const msg = e?.message || "";
      if (msg.toLowerCase().includes("user cancelled")) {
        // user cancelled the chooser — silent
      } else {
        toast.error(msg || "Could not connect");
      }
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = () => {
    try {
      hrCharRef.current?.stopNotifications();
      device?.gatt?.disconnect();
    } catch (e) {
      // ignore
    }
    if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    flushSamples();
    setConnected(false);
    setHr(null);
    toast.info("Disconnected");
  };

  const syncSteps = async () => {
    const n = parseInt(stepsInput, 10);
    if (!n || n < 0) {
      toast.error("Enter a valid step count");
      return;
    }
    setSyncing(true);
    try {
      await axios.post(`${API}/api/wearables/sync`, {
        device_id: deviceIdRef.current,
        device_name: deviceName || "Manual sync",
        steps_today: n,
      });
      updateToday({ steps: n });
      toast.success("Steps synced to dashboard");
      setStepsInput("");
    } catch (e) {
      toast.error("Could not sync steps");
    } finally {
      setSyncing(false);
    }
  };

  // Import Apple Health XML / Google Fit CSV — client-side parse
  const importFile = async (f) => {
    if (!f) return;
    try {
      const text = await f.text();
      let steps = null;
      if (f.name.endsWith(".xml") || text.startsWith("<?xml")) {
        // Apple Health: <Record type="HKQuantityTypeIdentifierStepCount" value="123" .../>
        const today = new Date().toISOString().slice(0, 10);
        const regex = /<Record[^>]+type="HKQuantityTypeIdentifierStepCount"[^>]+value="(\d+)"[^>]+startDate="([^"]+)"/g;
        let sum = 0;
        let m;
        while ((m = regex.exec(text)) !== null) {
          const dateStr = m[2].slice(0, 10);
          if (dateStr === today) sum += parseInt(m[1], 10);
        }
        steps = sum;
      } else if (f.name.endsWith(".csv") || text.includes(",")) {
        // Google Fit-style CSV: try "steps" column
        const lines = text.split(/\r?\n/);
        const header = (lines[0] || "").toLowerCase().split(",");
        const idx = header.findIndex((h) => h.trim().includes("step"));
        if (idx >= 0) {
          steps = lines.slice(1).reduce((acc, line) => {
            const cells = line.split(",");
            const n = parseInt(cells[idx], 10);
            return acc + (isNaN(n) ? 0 : n);
          }, 0);
        }
      }
      if (steps === null) {
        toast.error("Could not detect steps in this file.");
        return;
      }
      setStepsInput(String(steps));
      toast.success(`Detected ${steps} steps — click Sync to save`);
    } catch (e) {
      toast.error("Import failed");
    }
  };

  const avgHR = series.length
    ? Math.round(series.reduce((s, x) => s + x.bpm, 0) / series.length)
    : null;

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(59,130,246,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(0,229,255,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="wearables-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <Link
            to="/dashboard"
            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
          >
            Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-12">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
            Chapter 10 · Wearables
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            Your watch,{" "}
            <span className="italic text-cyan-300">on the same page.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            Connect any Bluetooth heart-rate device — Mi Band, Fitbit, Garmin,
            Apple Watch (via companion), Polar, Wahoo — and stream live BPM
            straight into your Twin.
          </p>
        </div>

        {!supported && (
          <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
            <div className="text-amber-200 font-semibold text-sm">
              Web Bluetooth pairing isn&apos;t available in this browser.
            </div>
            <p className="mt-2 text-sm text-amber-100/90 leading-relaxed">
              This is a browser-level restriction, not a bug on our side.
              Safari (iPhone / iPad / Mac) and Firefox don&apos;t support the
              Web Bluetooth API. On <b>iPhone / iPad</b>, every browser is
              forced to use Safari&apos;s engine — so Chrome for iOS won&apos;t
              help either.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <a
                href="https://apps.apple.com/us/app/bluefy-web-ble-browser/id1492822055"
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400/20 px-3 py-2 text-cyan-100"
              >
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                  iPhone / iPad
                </div>
                <div className="mt-0.5 text-white font-medium">
                  Install <u>Bluefy</u> (free)
                </div>
                <div className="text-[11px] text-cyan-200/80 mt-0.5">
                  Then open nutriplate07.com inside Bluefy.
                </div>
              </a>
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                  Android
                </div>
                <div className="mt-0.5 text-white font-medium">Chrome or Edge</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  Works out of the box.
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                  Desktop
                </div>
                <div className="mt-0.5 text-white font-medium">Chrome or Edge</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  Windows · macOS · Linux.
                </div>
              </div>
            </div>
            <div className="mt-4 text-[11px] text-amber-100/80 leading-relaxed">
              Meanwhile, you can log heart-rate readings from your watch face
              manually below — they&apos;ll still sync to your Twin.
            </div>
          </div>
        )}

        {/* Live pane */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4">
          {/* Connection card */}
          <div
            data-testid="wearable-connect-card"
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 flex flex-col"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              <Watch className="w-3.5 h-3.5" />
              Bluetooth device
            </div>

            <div className="mt-4 flex-1 flex flex-col items-center justify-center text-center">
              {connected ? (
                <>
                  <motion.div
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{ duration: 1.05, repeat: Infinity }}
                    className="w-24 h-24 rounded-full flex items-center justify-center"
                    style={{
                      background:
                        "radial-gradient(circle at center, rgba(239,68,68,0.6) 0%, rgba(239,68,68,0) 70%)",
                    }}
                  >
                    <Heart className="w-10 h-10 text-red-400 fill-current" />
                  </motion.div>
                  <div className="mt-3 font-display text-6xl font-semibold text-white tabular-nums">
                    {hr ?? "—"}
                  </div>
                  <div className="text-xs text-zinc-500">bpm · live</div>
                  <div className="mt-2 text-sm text-cyan-200">
                    {deviceName || "BLE device"}
                  </div>
                </>
              ) : (
                <>
                  <div className="w-24 h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                    <Bluetooth className="w-10 h-10 text-cyan-300" />
                  </div>
                  <div className="mt-3 font-display text-xl font-semibold text-white">
                    Not connected
                  </div>
                  <div className="mt-1 text-xs text-zinc-500 max-w-xs">
                    Make sure your watch is nearby and Bluetooth is on. We only
                    read heart-rate + battery.
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 flex gap-2">
              {connected ? (
                <button
                  data-testid="wearable-disconnect"
                  onClick={disconnect}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2.5 text-sm text-white"
                >
                  <BluetoothOff className="w-4 h-4" />
                  Disconnect
                </button>
              ) : (
                <button
                  data-testid="wearable-connect"
                  onClick={connect}
                  disabled={connecting || !supported}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-4 py-2.5 text-sm hover:bg-cyan-400 disabled:opacity-60"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Searching
                    </>
                  ) : (
                    <>
                      <Bluetooth className="w-4 h-4" /> Pair a device
                    </>
                  )}
                </button>
              )}
            </div>

            {(battery !== null || lastSyncAt) && (
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                    Battery
                  </div>
                  <div
                    className={`mt-0.5 font-display text-lg font-semibold tabular-nums ${
                      battery !== null && battery < 20 ? "text-red-300" : "text-emerald-300"
                    }`}
                  >
                    {battery !== null ? `${battery}%` : "—"}
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                    Last sync
                  </div>
                  <div className="mt-0.5 text-xs text-cyan-200">
                    {lastSyncAt || "—"}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Live chart */}
          <div
            data-testid="wearable-chart-card"
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 flex flex-col"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                <Activity className="w-3.5 h-3.5" />
                Live heart rate · last 60 samples
              </div>
              {avgHR && (
                <div className="text-[11px] text-zinc-400">
                  avg <span className="text-cyan-300 font-medium">{avgHR} bpm</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis
                    dataKey="t"
                    stroke="rgba(255,255,255,0.35)"
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={40}
                  />
                  <YAxis
                    domain={[40, 180]}
                    stroke="rgba(255,255,255,0.35)"
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0A0A14",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 12,
                      fontSize: 12,
                      color: "#fff",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="bpm"
                    stroke="#f87171"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {!connected && series.length === 0 && (
              <div className="mt-2 text-[11px] text-zinc-500 text-center">
                Pair a device to see live samples here.
              </div>
            )}
          </div>
        </div>

        {/* Step sync */}
        <div
          data-testid="steps-sync-card"
          className="mt-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <Footprints className="w-3.5 h-3.5" />
            Steps sync
          </div>
          <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
            Steps aren&apos;t part of the standard BLE profile, so we sync them
            two ways: type today&apos;s count from your watch, or import an
            export file from Apple Health (.xml) or Google Fit (.csv).
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
                Manual sync
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="wearable-steps-input"
                  type="number"
                  min="0"
                  value={stepsInput}
                  onChange={(e) => setStepsInput(e.target.value)}
                  placeholder="e.g., 8420"
                  className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-white focus:outline-none focus:border-cyan-400"
                />
                <button
                  data-testid="wearable-steps-sync"
                  onClick={syncSteps}
                  disabled={syncing || !stepsInput}
                  className="rounded-full bg-emerald-400 text-black font-medium px-4 py-2 text-sm hover:bg-emerald-300 disabled:opacity-60"
                >
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sync"}
                </button>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
                Import from Apple Health / Google Fit
              </div>
              <label
                data-testid="wearable-import-drop"
                className="cursor-pointer flex items-center gap-3 rounded-full border-2 border-dashed border-white/15 px-4 py-2 hover:bg-white/5"
              >
                <Upload className="w-4 h-4 text-cyan-300" />
                <span className="text-sm text-zinc-300">
                  Choose a .xml or .csv export…
                </span>
                <input
                  type="file"
                  accept=".xml,.csv,text/xml,text/csv"
                  onChange={(e) => importFile(e.target.files?.[0])}
                  className="hidden"
                />
              </label>
              <p className="mt-2 text-[11px] text-zinc-500">
                We parse the file in your browser — nothing is uploaded.
              </p>
            </div>
          </div>
        </div>

        {/* Compatibility */}
        <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
            <Radio className="w-3.5 h-3.5" />
            What connects
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {[
              "Mi Smart Band 8 / 9",
              "Fitbit (with BLE HR)",
              "Garmin Forerunner",
              "Polar Verity / H10",
              "Wahoo TICKR",
              "Coros Pace",
              "Apple Watch (via BLE relay)",
              "Any BLE HRM",
            ].map((n) => (
              <div
                key={n}
                className="rounded-2xl border border-white/10 bg-black/30 px-3 py-2 text-xs text-zinc-200"
              >
                {n}
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-cyan-300 flex-shrink-0" />
            The browser handles the pairing dialog. We only receive heart-rate
            values you consent to share — nothing else on the watch is accessed.
          </div>
        </div>

        {/* Emergency HR banner */}
        {hr && (hr > 160 || hr < 45) && connected && (
          <div className="mt-6 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3">
            <Zap className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-red-200 font-semibold">
                Heart rate is outside a common resting range.
              </div>
              <div className="mt-1 text-sm text-red-100">
                If this is unexpected and you feel unwell, stop activity and
                consider calling your clinician or 108.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
